import { useEffect, useState } from 'react';
import { ContentCard, ContentCardSkeleton } from '@/components/ContentCard';
import { Zap, Gamepad2, Film, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VortexPage({ setTheaterContent }) {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const url = filter === 'all' 
          ? `${API}/content/vortex`
          : `${API}/content/vortex?content_type=${filter}`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setContent(data);
        }
      } catch (error) {
        console.error('Failed to fetch vortex:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [filter]);

  const handleContentClick = async (item) => {
    try {
      const response = await fetch(`${API}/content/${item.content_id}`);
      if (response.ok) {
        const data = await response.json();
        setTheaterContent(data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="vortex-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1a0000] to-[#0a0000] border border-[#330000] p-6">
        <div className="flex items-center gap-3 mb-3">
          <Zap size={32} className="text-[#cc0000]" />
          <h1 className="text-3xl font-black">
            <span className="text-[#cc0000]">THE</span>{' '}
            <span className="text-white">VORTEX</span>
          </h1>
        </div>
        <p className="text-[#999] text-sm max-w-2xl">
          New submissions enter the Vortex for 24 hours. Content needs a rating of 2.0 or higher 
          to survive. Below that? <span className="text-[#ff0000]">BLAMMED!</span> Vote wisely, soldier.
        </p>
      </div>

      {/* Warning banner */}
      <div className="flex items-center gap-3 p-3 bg-[#1a1a00] border border-[#333300]">
        <AlertTriangle size={20} className="text-[#ffcc00] flex-shrink-0" />
        <p className="text-xs text-[#cccc00]">
          <strong>JUDGMENT SYSTEM:</strong> Content under judgment can be voted on by logged-in users. 
          After 24 hours, content with less than 2.0 average rating gets BLAMMED from existence!
        </p>
      </div>

      {/* Filter tabs */}
      <Tabs defaultValue="all" onValueChange={setFilter}>
        <TabsList className="bg-[#111] border border-[#333] p-1">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-[#cc0000] data-[state=active]:text-white text-xs px-4"
            data-testid="filter-all"
          >
            ALL
          </TabsTrigger>
          <TabsTrigger 
            value="game" 
            className="data-[state=active]:bg-[#cc0000] data-[state=active]:text-white text-xs px-4"
            data-testid="filter-games"
          >
            <Gamepad2 size={14} className="mr-1" />
            GAMES
          </TabsTrigger>
          <TabsTrigger 
            value="video" 
            className="data-[state=active]:bg-[#cc0000] data-[state=active]:text-white text-xs px-4"
            data-testid="filter-videos"
          >
            <Film size={14} className="mr-1" />
            MOVIES
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <ContentGrid content={content} loading={loading} onClick={handleContentClick} />
        </TabsContent>
        <TabsContent value="game" className="mt-4">
          <ContentGrid content={content} loading={loading} onClick={handleContentClick} />
        </TabsContent>
        <TabsContent value="video" className="mt-4">
          <ContentGrid content={content} loading={loading} onClick={handleContentClick} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ContentGrid = ({ content, loading, onClick }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array(12).fill(0).map((_, i) => <ContentCardSkeleton key={i} />)}
      </div>
    );
  }

  if (content.length === 0) {
    return (
      <div className="p-12 text-center border border-dashed border-[#333]">
        <Zap size={48} className="mx-auto mb-4 text-[#333]" />
        <p className="text-[#666]">The Vortex is empty. Be the first to submit!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="vortex-grid">
      {content.map((item) => (
        <ContentCard 
          key={item.content_id}
          content={item}
          onClick={() => onClick(item)}
        />
      ))}
    </div>
  );
};
